/**
 * Bulk "Mark as PAP" confirmation (product-sync Products tab).
 *
 * CONFIRM ONLY — this component owns no mutation. It states the blast radius
 * (count + the full list of titles + the classification consequence, same copy
 * tone as the single-product `MarkAsPapButton`) and hands control back to the
 * page, which runs the sequential loop and reports progress in the floating
 * selection bar. Keeping the loop out of the dialog is deliberate: the dialog
 * closes on confirm so the operator can keep reading the list while a
 * multi-minute run proceeds.
 *
 * OVER-CAP IS A STATE, NOT A TRUNCATION. Above `maxPerRun` the dialog refuses
 * and asks the operator to narrow the selection — silently marking "the first
 * 50" would leave them guessing which 50 changed classification.
 */
import { useTranslation } from 'react-i18next';
import { PackageCheck } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { SelectedProductLite } from '../lib/types';

export interface BulkMarkAsPapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: SelectedProductLite[];
  /** Max products per run; above this the dialog renders the refusal state. */
  maxPerRun: number;
  /** Fired on confirm. The dialog is already closing — the caller owns the loop. */
  onConfirm: () => void;
}

export function BulkMarkAsPapDialog({ open, onOpenChange, products, maxPerRun, onConfirm }: BulkMarkAsPapDialogProps) {
  const { t } = useTranslation('productSync');
  const overCap = products.length > maxPerRun;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {overCap ? t('bulkPap.tooManyTitle') : t('bulkPap.dialogTitle', { count: products.length })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {overCap
              ? t('bulkPap.tooManyBody', { count: products.length, max: maxPerRun })
              : t('bulkPap.dialogBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!overCap && products.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('bulkPap.listLabel')}
            </p>
            <ul className="max-h-52 divide-y overflow-y-auto rounded-md border">
              {products.map((p) => (
                <li key={p.id} className="truncate px-3 py-1.5 text-xs">{p.title}</li>
              ))}
            </ul>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>{overCap ? t('bulkPap.tooManyClose') : t('bulkPap.cancel')}</AlertDialogCancel>
          {!overCap && (
            <AlertDialogAction onClick={onConfirm} className="gap-1.5">
              <PackageCheck className="h-4 w-4" aria-hidden="true" />
              {t('bulkPap.confirm', { count: products.length })}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
